"use client";

import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import React, {
	type CSSProperties,
	useCallback,
	useEffect,
	useState,
} from "react";
import toast from "react-hot-toast";
import { useAuth } from "@/hooks/use-auth";
import type { FormSchema } from "@/lib/database";
import DemoFormBuilder from "@/components/form-builder/form-builder/demo-form-builder";
import { FormPreview } from "@/components/form-builder/form-preview";
import { DemoAIGenerator } from "./demo-ai-generator";
import { DemoSignupModal } from "./demo-signup-modal";
import { Badge, Card } from "../ui";
import { Button } from "../ui/button";
import { Loader } from "../ui/loader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

interface EmbeddedFormProps {
	className?: string;
	style?: CSSProperties;
}

const IFRAME_ORIGIN = "https://forms.projme.ru";
const DEFAULT_IFRAME_HEIGHT = 850;

export const EmbeddedForm = React.memo(function EmbeddedForm({
	className,
	style,
}: EmbeddedFormProps) {
	const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);
	const [hasError, setHasError] = useState(false);

	const handleMessage = useCallback((event: MessageEvent) => {
		if (event.origin !== IFRAME_ORIGIN) return;
		if (
			event.data?.type === "resize" &&
			typeof event.data?.height === "number"
		) {
			setIframeHeight(event.data.height);
		}
	}, []);

	React.useEffect(() => {
		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [handleMessage]);

	const iframeStyle = React.useMemo<CSSProperties>(
		() => ({
			width: "100%",
			height: `${iframeHeight}px`,
			border: "1px solid #ffffff",
			borderRadius: "22px",
			display: "block",
			margin: "0 auto",
			...style,
		}),
		[iframeHeight, style]
	);

	if (hasError) {
		return (
			<div
				aria-live="polite"
				className="flex w-full flex-col items-center justify-center gap-4 rounded-lg border border-destructive/20 bg-destructive/5 p-8 text-center"
				role="alert"
			>
				<p className="font-medium text-destructive">
					Unable to load form demo. Please try refreshing the page.
				</p>
				<Button
					onClick={() => {
						setHasError(false);
						window.location.reload();
					}}
					size="sm"
					variant="outline"
				>
					Reload
				</Button>
			</div>
		);
	}

	return (
		<div className={`flex w-full justify-center ${className || ""}`}>
			<iframe
				allow="clipboard-write; camera; microphone"
				frameBorder="0"
				loading="lazy"
				onError={() => setHasError(true)}
				referrerPolicy="no-referrer"
				sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
				src="https://forms.projme.ru/f/form-0gqpeq"
				style={iframeStyle}
				title="forms.projme demo form"
			/>
		</div>
	);
});

EmbeddedForm.displayName = "EmbeddedForm";

const noop = () => undefined;

/**
 * Планшетный сценарий главной: порог 1024 px совпадает с порогом самих конструкторов
 * (DemoFormBuilder и form-builder считают мобильным `window.innerWidth <= 1024`).
 * С прежними 767 px в диапазоне 768–1024 px в фиксированное окно демо-блока (765 px,
 * h-[56.25rem] при масштабе корня 0.85) монтировалась мобильная вёрстка `h-svh`,
 * чей низ обрезался без прокрутки.
 * Результат генерации на планшете показываем витриной без редактора (t_1c710027).
 */
function useIsMobileLayout() {
	const [isMobile, setIsMobile] = useState<boolean | null>(null);

	useEffect(() => {
		const query = window.matchMedia("(max-width: 1024px)");
		const update = () => setIsMobile(query.matches);
		update();
		query.addEventListener("change", update);
		return () => query.removeEventListener("change", update);
	}, []);

	return isMobile;
}

export default function Hero() {
	const t = useTranslations("home.hero");
	const tDemo = useTranslations("home.demoAi");
	const router = useRouter();
	const { user } = useAuth();
	// Результат генерации показываем в панели демо-конструктора (t_4c03e049, t_1c710027)
	const [generatedSchema, setGeneratedSchema] = useState<FormSchema | null>(
		null
	);
	// Идёт генерация: чат скрыт, вместо него показываем загрузку демо-конструктора
	const [isGenerating, setIsGenerating] = useState(false);
	// Переключатель вкладок управляемый: генерация открывает панель с результатом
	const [activeTab, setActiveTab] = useState("form-builder-demo");
	const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const isMobileViewport = useIsMobileLayout();

	// Карточка с демо-конструктором и вкладкой «Демо формы» появляется только после отправки
	// запроса: до этого на странице только чат с ИИ (t_1c710027).
	const showBuilder = isGenerating || generatedSchema !== null;

	const handleRequestSignup = useCallback(() => {
		setIsSignupModalOpen(true);
	}, []);

	// Схема готова: чат уходит, показываем демо-конструктор с полями из запроса
	const handleGenerated = useCallback((schema: FormSchema) => {
		setGeneratedSchema(schema);
		setIsGenerating(false);
		setActiveTab("form-builder-demo");
	}, []);

	// Создание формы из демо-схемы: анониму показываем регистрацию, авторизованному создаём
	// форму в аккаунте и возвращаем её id (либо null, если создать не удалось).
	const createDemoForm = useCallback(
		async (schema: FormSchema): Promise<string | null> => {
			if (!user) {
				setIsSignupModalOpen(true);
				return null;
			}
			try {
				const response = await fetch("/api/forms", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ schema }),
				});
				const data = (await response.json()) as {
					id?: string;
					error?: string;
				};
				if (!(response.ok && data?.id)) {
					throw new Error(data?.error ?? "create_failed");
				}
				return data.id;
			} catch {
				toast.error(tDemo("saveFailed"));
				return null;
			}
		},
		[tDemo, user]
	);

	// «Сохранить форму» на мобильном: форма уходит в аккаунт, дальше — дашборд
	const handleSaveToDashboard = useCallback(
		async (schema: FormSchema) => {
			setIsSaving(true);
			const id = await createDemoForm(schema);
			setIsSaving(false);
			if (id) {
				router.push("/dashboard");
			}
		},
		[createDemoForm, router]
	);

	// «Редактировать» на мобильном и «Сохранить» в десктопном конструкторе: форма в аккаунт,
	// дальше — редактор формы
	const handleSaveToEditor = useCallback(
		async (schema: FormSchema) => {
			setIsSaving(true);
			const id = await createDemoForm(schema);
			setIsSaving(false);
			if (id) {
				router.push(`/form-builder/${id}`);
			}
		},
		[createDemoForm, router]
	);

	// Панель «Демо конструктора»: загрузка → результат. На мобильном результат — витрина
	// без редактирования (владелец: «всё плавающее, не цельное»), на десктопе — прежний
	// демо-конструктор (t_1c710027).
	let builderPanel: React.ReactNode = null;
	if (isGenerating) {
		// Высота загрузки совпадает с высотой витрины (мобильный) и контейнера конструктора
		// (десктоп), поэтому окно не сужается и не разворачивается.
		builderPanel = (
			<div className="flex min-h-[32rem] w-full items-center justify-center gap-3 text-muted-foreground text-sm md:h-[56.25rem] md:min-h-0">
				<Loader />
				<span>{tDemo("loading")}</span>
			</div>
		);
	} else if (generatedSchema && isMobileViewport === null) {
		// Первый кадр до замера ширины: держим высоту, но не показываем не ту вёрстку
		builderPanel = (
			<div className="min-h-[32rem] w-full md:h-[56.25rem] md:min-h-0" />
		);
	} else if (generatedSchema) {
		builderPanel = isMobileViewport ? (
			<div className="flex min-h-[32rem] w-full flex-col gap-4 px-4 py-6">
				{/* Витрина готовой формы: тот же рендер, что в превью конструктора, но без
				    редактирования — поля не кликаются и скрыты от скринридера. */}
				<div
					aria-hidden="true"
					className="pointer-events-none select-none overflow-hidden rounded-xl border border-border bg-background"
					data-slot="demo-result-preview"
				>
					<FormPreview
						onFieldDelete={noop}
						onFieldSelect={noop}
						onFieldsReorder={noop}
						schema={generatedSchema}
						selectedFieldId={null}
					/>
				</div>
				<div className="flex flex-col gap-2">
					<Button
						className="min-h-[2.75rem] w-full"
						disabled={isSaving}
						onClick={() => handleSaveToDashboard(generatedSchema)}
					>
						{tDemo("saveForm")}
					</Button>
					<Button
						className="min-h-[2.75rem] w-full"
						disabled={isSaving}
						onClick={() => handleSaveToEditor(generatedSchema)}
						variant="outline"
					>
						{tDemo("editForm")}
					</Button>
				</div>
			</div>
		) : (
			<div className="h-[56.25rem] w-full overflow-hidden">
				<DemoFormBuilder
					initialSchema={generatedSchema}
					onSave={handleSaveToEditor}
				/>
			</div>
		);
	}

	return (
		<>
			<section
				aria-labelledby="home-hero-title"
				className="mx-auto flex w-full max-w-7xl flex-col bg-linear-to-t from-10% from-background to-85% to-card"
			>
				{/* Заголовок страницы: показывается и с чатом, и с результатом генерации
				    (t_1c710027). На мобильном текст другой, «бесплатно» выделено цветом и курсивом;
				    внизу отступ, чтобы сгенерированные поля не стояли впритык к тексту. */}
				<div className="w-full bg-card px-4 pt-10 pb-6 text-center md:px-8 md:pt-20 md:pb-0">
					<h1
						className="text-balance font-semibold text-2xl leading-tight md:text-3xl"
						id="home-hero-title"
					>
						{tDemo.rich("title", {
							accent: (chunks) => (
								<em className="text-primary italic">{chunks}</em>
							),
						})}
					</h1>
				</div>

				{/* Контейнер демо-блока смонтирован всегда, поэтому якорь /#form-builder-demo
				    не зависит от состояния страницы (t_1c710027). */}
				<div className="w-full" id="form-builder-demo">
					{/* Шаг 1: только чат с ИИ. Пока идёт генерация, чат остаётся смонтированным —
					    иначе оборвался бы запрос — и скрыт, вместо него загрузка конструктора. */}
					{!generatedSchema && (
						<div className={isGenerating ? "hidden" : undefined}>
							<DemoAIGenerator
								onGenerated={handleGenerated}
								onGeneratingChange={setIsGenerating}
								onLimitReached={handleRequestSignup}
							/>
						</div>
					)}

					{/* Шаги 2–3: загрузка демо-конструктора, затем конструктор с полями и вкладкой
					    «Демо формы». */}
					{showBuilder && (
						<Card className="w-full max-w-7xl rounded-none border-b-0 bg-card shadow-none flex flex-col py-0">
							<Tabs
								className="w-full flex flex-col"
								onValueChange={setActiveTab}
								value={activeTab}
							>
								<div className="flex items-center justify-center border-border border-b px-4 py-4 md:px-6">
									<TabsList>
										<TabsTrigger value="form-builder-demo">
											{t("tabBuilder")}
										</TabsTrigger>
										<TabsTrigger value="form-demo">{t("tabForm")}</TabsTrigger>
									</TabsList>
								</div>
								<TabsContent className="mt-0" value="form-demo">
									<EmbeddedForm className="bg-card" />
								</TabsContent>
								<TabsContent className="mt-0" value="form-builder-demo">
									{builderPanel}
								</TabsContent>
							</Tabs>
						</Card>
					)}
				</div>
			</section>

			<DemoSignupModal
				mode={user ? "limit" : "signup"}
				onOpenChange={setIsSignupModalOpen}
				open={isSignupModalOpen}
			/>
		</>
	);
}
